/*
 * Copyright 2026 Sony Corporation
 */

import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RouterIcon from '@mui/icons-material/Router';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import { Box, Button, Divider, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import type { Device } from '@ips-device-verse/twin-service/schema';

import { bomCsv } from '../../catalog/bom';
import { PanelHead } from './PanelHead';
import type { Transform } from '../../scene/types';

export type BomSection = 'bom' | 'configuration' | 'pose' | 'gateway';

export interface DeployPanelProps {
  worldName: string;
  devices: Device[];
  /** Keyed by device id; absent for a device not placed in the scene yet. */
  poses: Record<string, Transform>;
  selected: BomSection | undefined;
  onSelect: (section: BomSection) => void;
  onHide: () => void;
}

const SECTIONS: { id: BomSection; label: string; description: string; icon: React.ReactNode }[] = [
  { id: 'bom', label: 'BOM', description: 'Model, number, price', icon: <ReceiptLongIcon fontSize="small" /> },
  {
    id: 'configuration',
    label: 'Configuration',
    description: 'Network, look',
    icon: <SettingsEthernetIcon fontSize="small" />,
  },
  {
    id: 'pose',
    label: 'Device Pose',
    description: 'Position and rotation',
    icon: <ThreeDRotationIcon fontSize="small" />,
  },
  // What the world is made of ends with what is driving it.
  { id: 'gateway', label: 'Gateway', description: 'Connection and twins', icon: <RouterIcon fontSize="small" /> },
];

const download = (worldName: string, devices: Device[], poses: Record<string, Transform>) => {
  const blob = new Blob([bomCsv(worldName, devices, poses)], { type: 'text/csv;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = `bom-${worldName.replace(/[^\w.-]+/g, '-').toLowerCase()}.csv`;
  anchor.click();
  URL.revokeObjectURL(href);
};

/**
 * What it takes to build this world for real: the parts, how they are set up,
 * where they go and what drives them. Choosing an entry opens its table over
 * the viewport; nothing is itemised here.
 */
export const DeployPanel = ({ worldName, devices, poses, selected, onSelect, onHide }: DeployPanelProps) => (
  <Box className="flex h-full flex-col">
    <PanelHead title="Deploy" onHide={onHide} />

    <List dense className="flex-1 overflow-y-auto">
      {SECTIONS.map((section) => (
        <ListItemButton key={section.id} selected={section.id === selected} onClick={() => onSelect(section.id)}>
          <ListItemIcon className="min-w-9">{section.icon}</ListItemIcon>
          <ListItemText primary={section.label} secondary={section.description} />
        </ListItemButton>
      ))}
    </List>

    <Divider />
    <Box className="p-3">
      <Button
        startIcon={<DownloadIcon />}
        onClick={() => download(worldName, devices, poses)}
        disabled={devices.length === 0}
        fullWidth
        size="small"
      >
        Export CSV
      </Button>
    </Box>
  </Box>
);
